import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import type { MeResponse, PublicHandover } from "@handvero/shared";
import { api, errorText } from "../api";
import "./unterlagen.css";

/* Seite für den Kunden: Unterlagen eines Betriebs ansehen (ohne Konto) und mit einem Privatkonto in „Mein Zuhause“ übernehmen. */

const day = (iso: string) => new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
const size = (n: number) => (n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);

export function Uebergabe() {
  const { token = "" } = useParams();
  const [data, setData] = useState<PublicHandover | null>(null);
  const [error, setError] = useState("");
  const [me, setMe] = useState<MeResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ projectId: string; folderId: string; files: number } | null>(null);
  const [claimError, setClaimError] = useState("");
  const base = `/api/public/uebergabe/${encodeURIComponent(token)}`;

  useEffect(() => {
    document.title = "Ihre Unterlagen";
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex, nofollow";
    document.head.appendChild(robots);
    api.get<PublicHandover>(base).then(
      (d) => {
        setData(d);
        document.title = `${d.company}: Ihre Unterlagen`;
      },
      (err) => setError(errorText(err)),
    );
    api.get<MeResponse>("/api/me").then(setMe, () => setMe(null));
    return () => robots.remove();
  }, [base]);

  async function claim() {
    setBusy(true);
    setClaimError("");
    try {
      setDone(await api.post(`${base}/claim`));
    } catch (err) {
      setClaimError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const here = `/uebergabe/${token}`;
  const isPrivate = me?.company.accountKind === "private";
  return (
    <div className="ul-page">
      <main className="ul-card">
        {error ? (
          <>
            <h1>Link nicht verfügbar</h1>
            <p className="ul-error" role="alert">
              {error}
            </p>
          </>
        ) : !data ? (
          <div className="ul-loading" role="status" aria-label="Wird geladen" />
        ) : (
          <>
            <h1>Ihre Unterlagen von {data.company}</h1>
            <p className="ul-note">Zu „{data.projectName}“. Sie können alles ansehen und herunterladen, ohne Konto.</p>
            {data.message && (
              <blockquote className="ul-msg">
                <p>{data.message}</p>
                <cite>{data.company}</cite>
              </blockquote>
            )}

            <div className="ul-note" style={{ display: "grid", gap: 8 }}>
              {done ? (
                <>
                  <b>Übernommen: {done.files} {done.files === 1 ? "Datei" : "Dateien"} liegen jetzt in „Mein Zuhause“.</b>
                  <Link className="ul-btn" to={`/app/p/${done.projectId}/f/${done.folderId}`}>
                    Zur Hausakte
                  </Link>
                </>
              ) : isPrivate ? (
                <>
                  <b>In meine Hausakte übernehmen</b>
                  <span>Die Dateien werden in Ihr Konto kopiert und zählen zu Ihrem Speicher.</span>
                  <button className="ul-btn" onClick={() => void claim()} disabled={busy}>
                    {busy ? "Wird übernommen …" : "Jetzt übernehmen"}
                  </button>
                  {claimError && <span className="ul-error">{claimError}</span>}
                </>
              ) : me ? (
                <span>Sie sind mit einem Firmenkonto angemeldet. Die Übernahme in die Hausakte geht mit einem Privatkonto („HandVero Zuhause“).</span>
              ) : (
                <>
                  <b>Dauerhaft aufbewahren?</b>
                  <span>Mit einem kostenlosen Konto bei „HandVero Zuhause“ übernehmen Sie die Unterlagen mit einem Klick in Ihre eigene Hausakte.</span>
                  <span className="ul-btns">
                    <Link className="ul-btn" to="/privat/registrieren">
                      Kostenlos anmelden
                    </Link>
                    <Link className="ul-btn ghost" to={`/anmelden?weiter=${encodeURIComponent(here)}`}>
                      Ich habe schon ein Konto
                    </Link>
                  </span>
                </>
              )}
            </div>

            {data.files.length === 0 ? (
              <p>In dieser Übergabe liegen noch keine Dateien.</p>
            ) : (
              <ul className="ul-list">
                {data.files.map((f) => (
                  <li key={f.id}>
                    <span className="ul-ico" aria-hidden="true">
                      {f.image ? "FOTO" : f.kind === "pdf" ? "PDF" : "DATEI"}
                    </span>
                    <span className="ul-text">
                      <b>{f.name}</b>
                      <small>
                        {f.folder.replace(/^\d+_/, "")} · {size(f.size)}
                      </small>
                    </span>
                    <span className="ul-btns">
                      <a className="ul-btn ghost" href={`${base}/files/${f.id}`} target="_blank" rel="noreferrer">
                        Ansehen
                      </a>
                      <a className="ul-btn" href={`${base}/files/${f.id}?download=1`} download>
                        Herunterladen
                      </a>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <p className="ul-note">Dieser Link ist bis zum {day(data.expiresAt)} gültig. Speichern Sie die Dateien oder übernehmen Sie sie, wenn Sie sie länger brauchen.</p>
          </>
        )}
      </main>
      <footer className="ul-foot">
        Bereitgestellt mit{" "}
        <a href="/" className="ul-brand">
          <span>Hand</span>Vero
        </a>{" "}
        · <a href="/rechtliches">Impressum und Datenschutz</a>
      </footer>
    </div>
  );
}
